import React, { useState } from 'react'
import { api } from '../../utils/api'

/**
 * Place a collection On-Hold, or lift the hold (spec 20260911).
 *
 * When a customer requests changes but has not supplied the details, staff pause
 * the collection so it cannot be booked with wrong information. A hold needs a
 * reason (what is still outstanding); lifting clears it. The control mutates the
 * shipment in place and calls `onChanged` so the panel re-renders and its
 * dispatch buttons lock, matching the CollectionAddressEditor pattern. The
 * server is the real guard — this is the visible, convenient half.
 */
export default function CollectionHoldControl({ shipment, onChanged }) {
  const held = !!shipment.collection_on_hold
  const [editing, setEditing] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function place() {
    const trimmed = reason.trim()
    if (!trimmed) {
      setError('A reason is required to place a hold')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.patch(`/api/deals/collections/${shipment.id}/hold`, { hold: true, reason: trimmed })
      shipment.collection_on_hold = true
      shipment.collection_hold_reason = trimmed
      setEditing(false)
      setReason('')
      onChanged?.()
    } catch (e) {
      console.error('Failed to place hold:', e)
      setError(`Failed to place hold: ${e.message}`)
    } finally {
      setBusy(false)
    }
  }

  async function lift() {
    setBusy(true)
    setError(null)
    try {
      await api.patch(`/api/deals/collections/${shipment.id}/hold`, { hold: false })
      shipment.collection_on_hold = false
      shipment.collection_hold_reason = null
      onChanged?.()
    } catch (e) {
      setError(`Failed to lift hold: ${e.message}`)
    } finally {
      setBusy(false)
    }
  }

  if (held) {
    return (
      <div style={{
        background: 'rgba(184, 154, 46, 0.12)',
        border: '1px solid var(--color-warning, #b89a2e)',
        borderRadius: 'var(--radius)',
        padding: 'var(--space-3)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-warning, #8a6d1f)' }}>⏸ ON HOLD</div>
            {shipment.collection_hold_reason && (
              <div style={{ fontSize: 12, marginTop: 4 }}>{shipment.collection_hold_reason}</div>
            )}
          </div>
          <button className="btn btn-xs" onClick={() => { lift() }} disabled={busy} style={{ fontSize: 10, padding: '3px 10px' }}>
            {busy ? 'Lifting...' : 'Lift hold'}
          </button>
        </div>
        {error && <div style={{ fontSize: 11, color: 'var(--color-error)', marginTop: 6 }}>{error}</div>}
      </div>
    )
  }

  if (!editing) {
    return (
      <button
        className="btn"
        onClick={() => { setError(null); setEditing(true) }}
        style={{
          width: '100%',
          fontSize: 15,
          fontWeight: 700,
          padding: '12px 16px',
          background: 'var(--color-warning, #b89a2e)',
          color: '#1a1a1a',
          border: 'none',
          borderRadius: 'var(--radius)',
          cursor: 'pointer',
        }}
      >
        ⏸ Place on hold
      </button>
    )
  }

  return (
    <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)', padding: 'var(--space-2)' }}>
      <label htmlFor="hold-reason" style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
        Reason (what is still outstanding?)
      </label>
      <textarea
        id="hold-reason"
        value={reason}
        onChange={e => setReason(e.target.value)}
        rows={2}
        style={{ width: '100%', boxSizing: 'border-box', padding: '4px 6px', fontSize: 12, borderRadius: 4, border: '1px solid var(--color-border)', resize: 'vertical' }}
      />
      {error && <div style={{ fontSize: 11, color: 'var(--color-error)', marginTop: 4 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-xs" onClick={() => { setEditing(false); setReason(''); setError(null) }} style={{ fontSize: 10, padding: '3px 8px' }}>
          Cancel
        </button>
        <button
          className="btn btn-xs"
          onClick={() => { place() }}
          disabled={busy || !reason.trim()}
          style={{ fontSize: 10, padding: '3px 8px', background: 'var(--color-warning, #b89a2e)', color: '#1a1a1a', border: 'none', opacity: (busy || !reason.trim()) ? 0.6 : 1 }}
        >
          {busy ? 'Placing...' : 'Place on hold'}
        </button>
      </div>
    </div>
  )
}
